import { XIcon } from 'components/icons';
import { Skeleton } from 'components/ui/skeleton';
import get from 'lodash/get';
import { useEffect } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { optionDisplayLabel, useLastNameFirst } from 'utils/nameOrder';
import { DynamicMultiselectFilterForm, NULL_FILTER_SENTINEL, SelectOption, TextMatch } from '../ListTableTypes';
import { CONDITION_LABEL_IDS, FilterChip as FilterChipModel } from './filterChips';
import { chipClassName, TOGGLE_CHIP } from './QuickFilterChips';

export type ResolveSelectedOptions = (
    key: string,
    requestedValues: SelectOption['value'][],
    options: SelectOption[]
) => void;

const CHIP_MATCH_IDS: Record<TextMatch, string> = {
    any: 'filters.contains-any-of',
    all: 'filters.contains-all-of',
    none: 'filters.contains-none-of',
};

type Props = {
    chip: FilterChipModel;
    onOpen: () => void;
    onRemove: () => void;
    onResolveOptions: ResolveSelectedOptions;
};

/**
 * One active filter: a click reopens + Filter on its field, × removes its clause. A yes / no filter is a toggle
 * instead, as a quick filter's single option is: a click removes it.
 */
export default function FilterChip({ chip, onOpen, onRemove, onResolveOptions }: Props) {
    const { field, label, condition, values, answer, answerLabel, match, title } = chip;
    const isUnresolved = values.some((value) => value === '');
    if (answer) {
        return (
            <button
                type="button"
                aria-pressed
                onClick={onRemove}
                // It sits in the wrapping row itself, outside the quick chips' scrolling line: it shrinks to truncate.
                className={cn(chipClassName({ isOn: true }), TOGGLE_CHIP, 'max-w-md min-w-0 shrink')}
            >
                <span className="truncate">
                    {answerLabel ?? (answer === 'no' ? t('filters.chip-no', { label }) : label)}
                </span>
            </button>
        );
    }
    return (
        // On touch × takes a 44px area, which reaches its label's across `gap-1`: the gap widens so they meet.
        <span className="bg-primary-tint border-primary-tint text-primary-ink text-meta inline-flex h-8 max-w-md min-w-0 items-center gap-1 rounded-full border ps-2.5 pe-1 pointer-coarse:gap-2.5">
            <button
                type="button"
                onClick={onOpen}
                title={title}
                className="hit-area relative flex min-w-0 cursor-pointer items-center gap-[5px] transition-[scale] duration-(--press-dur) ease-out motion-safe:not-aria-[haspopup]:will-change-transform motion-safe:active:not-aria-[haspopup]:scale-(--press-scale)"
            >
                <span className="text-primary-ink-muted shrink-0">{label}</span>
                {condition === 'is' && !match ? null : (
                    <span className="shrink-0">
                        {t(match ? CHIP_MATCH_IDS[match] : CONDITION_LABEL_IDS[condition])}
                    </span>
                )}
                {isUnresolved ? (
                    <Skeleton className="inline-block h-3.5 w-[70px]" />
                ) : values.length ? (
                    <span className="truncate font-semibold">{values.join(', ')}</span>
                ) : null}
            </button>
            <button
                type="button"
                aria-label={`${t('remove')} ${label}`}
                onClick={onRemove}
                className="hover:bg-primary-ink/10 hit-area relative flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full transition-[scale] duration-(--press-dur) ease-out motion-safe:not-aria-[haspopup]:will-change-transform motion-safe:active:not-aria-[haspopup]:scale-(--press-scale)"
            >
                <XIcon className="size-3" strokeWidth={2} />
            </button>
            {isUnresolved && field.kind === 'main' && field.filter.type === 'dynamicmultiselect' ? (
                <OptionLabelResolver fieldKey={field.key} filter={field.filter} onResolve={onResolveOptions} />
            ) : null}
        </span>
    );
}

/**
 * Fetches the options behind an entity picker's bare ids through the picker's own query, as its
 * `DynamicMultiselect` does when it mounts: the chip is often all that is mounted in the new design.
 */
function OptionLabelResolver({
    fieldKey,
    filter,
    onResolve,
}: {
    fieldKey: string;
    filter: DynamicMultiselectFilterForm;
    onResolve: ResolveSelectedOptions;
}) {
    const { paginated } = filter;
    const [fetchOptions] = paginated.fetch();
    const lastNameFirst = useLastNameFirst();
    const requestedKey = JSON.stringify(
        filter.selected.filter((option) => option.value !== NULL_FILTER_SENTINEL).map((option) => option.value)
    );

    useEffect(() => {
        const requested = JSON.parse(requestedKey) as SelectOption['value'][];
        const ids = requested.map((value) => (String(value).startsWith('"') ? value : `"${value}"`)).join(',');
        fetchOptions({
            filters: [paginated.filters, `${paginated.getOptionIdentifier}=in=(${ids})`].filter(Boolean).join(';'),
            page: 0,
            pageSize: 1000,
        }).then(({ data }) => {
            if (!data?.content) return;
            onResolve(
                fieldKey,
                requested,
                data.content.map((item) => ({
                    ...item,
                    value: get(item, paginated.getOptionIdentifier),
                    label: optionDisplayLabel(item, lastNameFirst, get(item, paginated.getOptionLabel)),
                }))
            );
        });
    }, [
        requestedKey,
        fetchOptions,
        fieldKey,
        lastNameFirst,
        onResolve,
        paginated.filters,
        paginated.getOptionIdentifier,
        paginated.getOptionLabel,
    ]);

    return null;
}
